/**
 * Componente de barra de uso de armazenamento
 * 
 * Exibe visualmente quanto do limite de 200MB foi utilizado
 * pelo tenant na galeria de mídia
 */

import { Progress } from '@/components/ui/progress';
import { formatBytes, TENANT_STORAGE_LIMIT } from '@/types/media';
import { cn } from '@/lib/utils';
import { HardDrive, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface MediaUsageBarProps {
  totalBytes: number;
  totalFiles: number;
  className?: string;
  /** Variante de exibição: 'default' (completa) ou 'compact' (linha única) */
  variant?: 'default' | 'compact';
}

export function MediaUsageBar({
  totalBytes,
  totalFiles,
  className,
  variant = 'default',
}: MediaUsageBarProps) {
  const percentageUsed = (totalBytes / TENANT_STORAGE_LIMIT) * 100;
  const remainingBytes = TENANT_STORAGE_LIMIT - totalBytes;
  const isNearLimit = percentageUsed >= 80;
  const isFull = percentageUsed >= 100;

  // Determinar cor da barra baseado no uso
  const getProgressColor = () => {
    if (isFull) return 'bg-destructive';
    if (isNearLimit) return 'bg-warning';
    return 'bg-primary';
  };

  // =========================================================================
  // Variante compacta: linha única com barra + texto resumido
  // =========================================================================
  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        <HardDrive className="h-4 w-4 text-muted-foreground shrink-0" />
        <Progress 
          value={Math.min(percentageUsed, 100)} 
          className="h-1.5 w-24 shrink-0"
          indicatorClassName={getProgressColor()}
        />
        <span className={cn(
          'text-xs whitespace-nowrap',
          isFull ? 'text-destructive font-medium' : 'text-muted-foreground'
        )}>
          {formatBytes(totalBytes)} / {formatBytes(TENANT_STORAGE_LIMIT)} · {totalFiles} {totalFiles === 1 ? 'arquivo' : 'arquivos'}
        </span>
        {isNearLimit && (
          <AlertTriangle className={cn(
            'h-3.5 w-3.5 shrink-0',
            isFull ? 'text-destructive' : 'text-warning'
          )} />
        )}
      </div>
    );
  }

  // =========================================================================
  // Variante padrão: layout completo com cabeçalho, barra e alerta
  // =========================================================================
  return (
    <div className={cn('space-y-3', className)}>
      {/* Cabeçalho com ícone e estatísticas */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Armazenamento</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {totalFiles} {totalFiles === 1 ? 'arquivo' : 'arquivos'}
        </span>
      </div>

      {/* Barra de progresso */}
      <div className="space-y-2">
        <Progress 
          value={Math.min(percentageUsed, 100)} 
          className="h-2"
          indicatorClassName={getProgressColor()}
        />
        
        <div className="flex items-center justify-between text-xs">
          <span className={cn(
            'font-medium',
            isFull && 'text-destructive',
            isNearLimit && !isFull && 'text-warning'
          )}>
            {formatBytes(totalBytes)} / {formatBytes(TENANT_STORAGE_LIMIT)}
          </span>
          <span className="text-muted-foreground">
            {percentageUsed.toFixed(1)}% utilizado
          </span>
        </div>
      </div>

      {/* Alerta quando próximo do limite */}
      {isNearLimit && (
        <Alert variant={isFull ? 'destructive' : 'default'} className="py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {isFull ? (
              <>
                <strong>Limite atingido!</strong> Exclua arquivos para liberar espaço.
              </>
            ) : (
              <>
                <strong>Atenção:</strong> Restam apenas {formatBytes(remainingBytes)} disponíveis.
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
